import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, useToast, Link,
} from '@chakra-ui/react';
import {
  recorridosApi, camionesApi, rutasApi, localidadesApi,
  TIPOS_VIALIDAD, Recorrido, Camion, Ruta, Localidad,
} from '../../../api/serviciosApi';
import MapSelector from '../../../components/MapSelector';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

export const ESTATUS_RECORRIDO = ['Pendiente', 'En proceso', 'Finalizado', 'Cancelado'];
export const estatusRecorridoColor = (e?: string) => {
  if (e === 'Finalizado') return 'green';
  if (e === 'En proceso') return 'blue';
  if (e === 'Cancelado') return 'red';
  return 'orange';
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);
const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);
const num = (v: string) => (v === '' || v == null ? undefined : Number(v));

const FormularioRecorrido: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [folio, setFolio] = useState(data?.folio || '');
  const [fecha, setFecha] = useState(data?.fecha ? String(data.fecha).split('T')[0] : new Date().toISOString().split('T')[0]);
  const [horaInicio, setHoraInicio] = useState(data?.horaInicio || '');
  const [horaTermino, setHoraTermino] = useState(data?.horaTermino || '');
  const [camionId, setCamionId] = useState(data?.camionId ? String(data.camionId) : '');
  const [rutaId, setRutaId] = useState(data?.rutaId ? String(data.rutaId) : '');
  const [operador, setOperador] = useState(data?.operador || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');
  // ubicación
  const [estado, setEstado] = useState(data?.estado || 'Tlaxcala');
  const [municipio, setMunicipio] = useState(data?.municipio || 'Totolac');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [colonia, setColonia] = useState(data?.colonia || '');
  const [codigoPostal, setCodigoPostal] = useState(data?.codigoPostal || '');
  const [tipoVialidad, setTipoVialidad] = useState(data?.tipoVialidad || '');
  const [vialidad, setVialidad] = useState(data?.vialidad || '');
  const [numeroExterior, setNumeroExterior] = useState(data?.numeroExterior || '');
  const [numeroInterior, setNumeroInterior] = useState(data?.numeroInterior || '');
  const [referencias, setReferencias] = useState(data?.referencias || '');
  const [latitud, setLatitud] = useState(data?.latitud != null ? String(data.latitud) : '');
  const [longitud, setLongitud] = useState(data?.longitud != null ? String(data.longitud) : '');
  const [urlMaps, setUrlMaps] = useState(data?.urlMaps || '');

  const [camiones, setCamiones] = useState<Camion[]>([]);
  const [rutas, setRutas] = useState<Ruta[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    camionesApi.list().then(setCamiones).catch(() => setCamiones([]));
    rutasApi.list().then(setRutas).catch(() => setRutas([]));
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!fecha || !camionId) {
      toast({ title: 'Campos obligatorios', description: 'La fecha y el camión son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const loc = localidades.find((l) => l.descripcion === localidad);
      const body: Partial<Recorrido> = {
        folio: folio || undefined, fecha, horaInicio, horaTermino,
        camionId: Number(camionId), rutaId: rutaId ? Number(rutaId) : undefined,
        operador, observaciones, estatus,
        estado, municipio, localidad, localidadId: loc?.id, colonia, codigoPostal,
        tipoVialidad, vialidad, numeroExterior, numeroInterior, referencias,
        latitud: num(latitud), longitud: num(longitud), urlMaps,
      };
      if (modo === 'edit') await recorridosApi.update(data.id, body);
      else await recorridosApi.create(body);
      toast({ title: modo === 'edit' ? 'Recorrido actualizado' : 'Recorrido registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

  if (modo === 'view') {
    const d: Recorrido = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información del Recorrido</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="ESTATUS" value={<Badge colorScheme={estatusRecorridoColor(d.estatus)}>{d.estatus}</Badge>} />
          <Field label="FECHA" value={fmt(d.fecha)} />
          <Field label="HORARIO" value={d.horaInicio ? `${d.horaInicio} — ${d.horaTermino || '?'}` : undefined} />
          <Field label="CAMIÓN" value={d.camion?.numeroEconomico} />
          <Field label="RUTA" value={d.ruta?.nombre} />
          <Field label="OPERADOR" value={d.operador} />
        </SimpleGrid>
        <SectionTitle>Ubicación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="ESTADO" value={d.estado} />
          <Field label="MUNICIPIO" value={d.municipio} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="VIALIDAD" value={[d.tipoVialidad, d.vialidad].filter(Boolean).join(' ')} />
          <Field label="NÚMERO" value={[d.numeroExterior, d.numeroInterior].filter(Boolean).join(' / ')} />
          <Field label="C.P." value={d.codigoPostal} />
          <Field label="REFERENCIAS" value={d.referencias} />
          <Field label="COORDENADAS" value={d.latitud && d.longitud ? (
            <Link color="blue.500" isExternal href={`https://maps.google.com/?q=${d.latitud},${d.longitud}`}>{d.latitud}, {d.longitud}</Link>
          ) : undefined} />
        </SimpleGrid>
        {d.observaciones && (<><SectionTitle>Observaciones</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text></>)}
        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información del Recorrido</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Folio</FormLabel>
          <Input placeholder="Automático si se deja vacío" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS_RECORRIDO.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Camión</FormLabel>
          <Select placeholder="Selecciona camión" value={camionId} onChange={(e) => setCamionId(e.target.value)}>
            {camiones.map((c) => <option key={c.id} value={c.id}>{c.numeroEconomico}{c.marca ? ` — ${c.marca}` : ''}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Ruta</FormLabel>
          <Select placeholder="Selecciona ruta" value={rutaId} onChange={(e) => setRutaId(e.target.value)}>
            {rutas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Operador</FormLabel>
          <Input value={operador} onChange={(e) => setOperador(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora inicio</FormLabel>
          <Input type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora término</FormLabel>
          <Input type="time" value={horaTermino} onChange={(e) => setHoraTermino(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Ubicación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl><FormLabel fontSize="sm">Estado</FormLabel><Input value={estado} onChange={(e) => setEstado(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Municipio</FormLabel><Input value={municipio} onChange={(e) => setMunicipio(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl><FormLabel fontSize="sm">Colonia</FormLabel><Input value={colonia} onChange={(e) => setColonia(e.target.value)} /></FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Select placeholder="Selecciona" value={tipoVialidad} onChange={(e) => setTipoVialidad(e.target.value)}>
            {TIPOS_VIALIDAD.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl><FormLabel fontSize="sm">Vialidad (nombre)</FormLabel><Input value={vialidad} onChange={(e) => setVialidad(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Número exterior</FormLabel><Input value={numeroExterior} onChange={(e) => setNumeroExterior(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Número interior</FormLabel><Input value={numeroInterior} onChange={(e) => setNumeroInterior(e.target.value)} /></FormControl>
        <FormControl><FormLabel fontSize="sm">Código postal</FormLabel><Input value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}><FormLabel fontSize="sm">Referencias</FormLabel><Input value={referencias} onChange={(e) => setReferencias(e.target.value)} /></FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={latitud ? Number(latitud) : null}
            initialLng={longitud ? Number(longitud) : null}
            onLocationSelect={(lat, lng, address) => {
              setLatitud(String(lat));
              setLongitud(String(lng));
              setUrlMaps(`https://www.google.com/maps?q=${lat},${lng}`);
              if (address?.neighborhood && !colonia) setColonia(address.neighborhood);
              if (address?.postalCode && !codigoPostal) setCodigoPostal(address.postalCode);
            }}
          />
          {latitud && longitud && (
            <Link fontSize="sm" color="blue.500" isExternal mt={1} display="inline-block"
              href={urlMaps || `https://www.google.com/maps?q=${latitud},${longitud}`}>
              Ver en Google Maps ({latitud}, {longitud})
            </Link>
          )}
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Observaciones</SectionTitle>
      <Textarea rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioRecorrido;
